import {
  ApolloLink,
  Observable,
  type FetchResult,
  type Operation,
} from '@apollo/client';
import {
  ORDER_FLAG_WRITES_WAIT_CAP_MS,
  hasPendingOrderFlagWrites,
  waitForPendingOrderFlagWrites,
} from './services/orderFlags';

/**
 * Barrera de «Solo marcadas» (QA de las banderitas en beta, 2026-10-23).
 *
 * Marcar una fila y pulsar Buscar con «Solo importantes» al momento (~100 ms)
 * mandaba el listado antes de que el servidor guardara la marca: la lista
 * salía sin la recién marcada hasta la búsqueda siguiente. Este link retiene
 * las operaciones cuyo `input.onlyFlagged` es `true` hasta que terminan las
 * `SetOrderFlags` que ya estaban en vuelo al llegar (éxito, error o encolada
 * sin red), con un tope de `ORDER_FLAG_WRITES_WAIT_CAP_MS`.
 *
 * - Sin marcas en vuelo, o sin `onlyFlagged`, pasa de largo (síncrono).
 * - Nunca retiene `SetOrderFlags` (no lleva `onlyFlagged`): la cola offline de
 *   más abajo la sigue viendo igual, y sin red se encola al instante, así que
 *   la espera dura lo que tarda en encolarse.
 * - Va el PRIMERO de la cadena: la espera ocurre antes del respaldo de caché
 *   (que arranca su plazo de consulta al suscribirse: así no lo gasta) y de la
 *   red.
 */

type BarrierDeps = {
  hasPendingWrites?: () => boolean;
  waitForPendingWrites?: (timeoutMs: number) => Promise<void>;
  timeoutMs?: number;
};

/** ¿Es un listado filtrado por «Solo marcadas»? */
export function isOnlyFlaggedOperation(operation: Operation): boolean {
  const variables = operation.variables as
    | { input?: { onlyFlagged?: unknown } }
    | undefined;
  return variables?.input?.onlyFlagged === true;
}

export function createOrderFlagsBarrierLink(deps: BarrierDeps = {}): ApolloLink {
  const hasPendingWrites = deps.hasPendingWrites ?? hasPendingOrderFlagWrites;
  const waitForPendingWrites =
    deps.waitForPendingWrites ?? waitForPendingOrderFlagWrites;
  const timeoutMs = deps.timeoutMs ?? ORDER_FLAG_WRITES_WAIT_CAP_MS;

  return new ApolloLink((operation, forward) => {
    if (!isOnlyFlaggedOperation(operation) || !hasPendingWrites()) {
      return forward(operation);
    }
    return new Observable<FetchResult>((observer) => {
      let cancelled = false;
      let subscription: { unsubscribe: () => void } | undefined;
      // Las marcas en vuelo se cuentan AQUÍ, al suscribirse la operación: las
      // que empiecen después no la retienen más.
      const proceed = () => {
        if (cancelled) return;
        subscription = forward(operation).subscribe({
          next: (result) => observer.next(result),
          error: (error) => observer.error(error),
          complete: () => observer.complete(),
        });
      };
      waitForPendingWrites(timeoutMs).then(proceed, proceed);
      return () => {
        cancelled = true;
        subscription?.unsubscribe();
      };
    });
  });
}
